import React, { useState, useEffect } from "react";
import { 
  FaReact, 
  FaNode, 
  FaPython, 
  FaAws, 
  FaDocker
} from "react-icons/fa";
import {
  SiSupabase,
  SiMongodb,
  SiSelenium,
  SiFlask,
  SiDjango,
  SiNextdotjs
} from "react-icons/si";

export const Skills = () => {
  const [visibleSkills, setVisibleSkills] = useState([]);

  // Only technologies mentioned in your original content
  const skillsData = [
    {
      name: "Selenium",
      icon: <SiSelenium />,
      level: 90,
      color: "from-green-400 to-green-600",
      category: "Testing"
    },
    {
      name: "Python",
      icon: <FaPython />,
      level: 95,
      color: "from-yellow-400 to-yellow-600",
      category: "Backend"
    },
    {
      name: "Pymongo",
      icon: <SiMongodb />,
      level: 85,
      color: "from-green-500 to-green-700",
      category: "Database"
    },
    {
      name: "Boto3",
      icon: <FaAws />,
      level: 80,
      color: "from-orange-400 to-orange-600",
      category: "Cloud"
    },
    {
      name: "Docker",
      icon: <FaDocker />,
      level: 80,
      color: "from-blue-500 to-blue-700",
      category: "DevOps"
    },
    {
      name: "AWS",
      icon: <FaAws />,
      level: 75,
      color: "from-orange-400 to-orange-600",
      category: "Cloud"
    },
    {
      name: "Flask",
      icon: <SiFlask />,
      level: 85,
      color: "from-gray-300 to-gray-500",
      category: "Backend"
    },
    {
      name: "Django",
      icon: <SiDjango />,
      level: 70,
      color: "from-green-600 to-green-800",
      category: "Backend"
    },
    {
      name: "React.js",
      icon: <FaReact />,
      level: 90,
      color: "from-blue-400 to-blue-600",
      category: "Frontend"
    },
    {
      name: "Supabase",
      icon: <SiSupabase />,
      level: 85,
      color: "from-emerald-400 to-emerald-600",
      category: "Backend"
    },
    {
      name: "Node.js",
      icon: <FaNode />,
      level: 80,
      color: "from-green-400 to-green-600",
      category: "Backend"
    },
    {
      name: "Next.js",
      icon: <SiNextdotjs />,
      level: 75,
      color: "from-gray-400 to-gray-600",
      category: "Frontend"
    }
  ];

  const categories = ["All", "Frontend", "Backend", "Cloud", "DevOps", "Database", "Testing"];
  const [selectedCategory, setSelectedCategory] = useState("All");

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisibleSkills(skillsData);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const filteredSkills = selectedCategory === "All" 
    ? skillsData 
    : skillsData.filter(skill => skill.category === selectedCategory);

  return (
    <section id="skills" className="py-20 bg-black/20 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'SF Pro, Inter, sans-serif' }}>
            Technical Skills
          </h2>
          <p className="text-gray-400 text-lg" style={{ fontFamily: 'SF Pro, Inter, sans-serif' }}>
            Technologies I've worked with
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-2 rounded-full transition-all duration-300 ${
                selectedCategory === category
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg shadow-blue-500/25'
                  : 'bg-slate-800/60 backdrop-blur-sm text-slate-300 hover:bg-slate-700/60 hover:shadow-lg hover:shadow-blue-500/10'
              }`}
              style={{ fontFamily: 'SF Pro, Inter, sans-serif' }}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Skills Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6">
          {filteredSkills.map((skill, index) => (
            <div
              key={skill.name}
              className="group relative bg-slate-800/60 backdrop-blur-md border border-blue-500/20 rounded-2xl p-6 hover:bg-slate-700/60 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/30 hover:border-blue-400/40"
              style={{
                animationDelay: `${index * 0.1}s`,
                animation: 'fadeIn 0.6s ease-in-out forwards'
              }}
            >
              {/* Skill Icon */}
              <div className="flex justify-center mb-4">
                <div className={`p-4 rounded-full bg-gradient-to-r ${skill.color} text-white text-2xl group-hover:scale-110 transition-transform duration-300`}>
                  {skill.icon}
                </div>
              </div>

              {/* Skill Name */}
              <h3 className="text-center text-white font-semibold mb-3" style={{ fontFamily: 'SF Pro, Inter, sans-serif' }}>
                {skill.name}
              </h3>

              {/* Skill Level */}
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-gray-400">
                  <span>Experience</span>
                  <span>{skill.level}%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full bg-gradient-to-r ${skill.color} transition-all duration-1000 ease-out`}
                    style={{ width: `${skill.level}%` }}
                  ></div>
                </div>
              </div>

              {/* Category Badge */}
              <div className="mt-3 text-center">
                <span className="px-2 py-1 bg-slate-800/60 backdrop-blur-sm rounded-full text-xs text-blue-300 border border-blue-500/20 shadow-sm">
                  {skill.category}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
